import Vue from 'vue';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import PipelinesAPI from '../../api/pipelines';

/** Monotonic id so stale async board loads cannot overwrite `records` after a newer fetch. */
let pipelineRecordsFetchGeneration = 0;

function nextPipelineRecordsFetchGeneration() {
  pipelineRecordsFetchGeneration += 1;
  return pipelineRecordsFetchGeneration;
}

function isLatestPipelineRecordsFetch(gen) {
  return gen === pipelineRecordsFetchGeneration;
}

function normalizePipelineBoardId(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function boardIdFromFilterContactData(data) {
  if (!data || typeof data !== 'object') return null;
  if (
    data.board_id !== undefined &&
    data.board_id !== null &&
    data.board_id !== ''
  ) {
    return normalizePipelineBoardId(data.board_id);
  }
  if (
    data.key4 === 'board_id' &&
    data.value4 !== undefined &&
    data.value4 !== null &&
    data.value4 !== ''
  ) {
    return normalizePipelineBoardId(data.value4);
  }
  return null;
}

function boardIdFromLazyLoadData(data) {
  if (!data || typeof data !== 'object') return null;
  if (
    data.key4 === 'board_id' &&
    data.value4 !== undefined &&
    data.value4 !== null &&
    data.value4 !== ''
  ) {
    return normalizePipelineBoardId(data.value4);
  }
  return null;
}

/** Pipeline status id from lazy-load payload (`value` when `key` is status_id). */
function normalizeLazyLoadStatusId(data) {
  if (!data || typeof data !== 'object') return null;
  const raw = data.value;
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function contactsFromPipelineColumns(columns) {
  if (!Array.isArray(columns)) {
    return [];
  }

  return columns.flatMap(column =>
    Array.isArray(column.items) ? column.items : []
  );
}

function hydratePipelineContactLabels({ dispatch }, contacts) {
  dispatch(
    'contactLabels/hydrateFromPipelineContacts',
    contacts,
    { root: true }
  );
}

export const state = {
  records: [],
  status_data: [],
  boards: [],
  agents:[],
  reminderCount: 0,
  pastReminderCount:0,
  appliedBoardFilters: [],
  appliedBoardFiltersByBoard: {},
  currentBoardFilterPayload: null,
  /** Board id for the data currently in `records`; used to ignore stale lazy-load responses. */
  currentPipelineBoardId: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isCreateState: false,
    isCreateBoard: false,
    isFetchingContacts: false,
    islazyloading: false
  },
  pipelines_contact: {}
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  //   getCampaigns: _state => campaignType => {
  //     return _state.records.filter(
  //       record => record.campaign_type === campaignType
  //     );
  //   },
  getAllState: _state => {
    return _state.records;
  },
  getAllBoards: _state => {
    return _state.boards;
  },
  getAllStatus: _state => {
    return _state.status_data;
  },
  getAllContacts: _state => {
    return _state.piplines_contact;
  },
  getAllAssignee: _state => {
    return _state.agents;
  },
  getReminderCount: _state =>{
    return {upcoming:_state.reminderCount, past:_state.pastReminderCount}
  },
  getAppliedBoardFilters: _state => {
    return _state.appliedBoardFilters;
  },
  getAppliedBoardFiltersByBoard: _state => boardId => {
    if (!boardId) return [];
    const key = String(boardId);
    return _state.appliedBoardFiltersByBoard[key] || [];
  },
  getCurrentBoardFilterPayload: _state => _state.currentBoardFilterPayload,
};

export const actions = {
  prepareBoardFetch({ commit }) {
    nextPipelineRecordsFetchGeneration();
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    commit(types.SET_CAMPAIGNS, []);
    commit(types.SET_CURRENT_PIPELINE_BOARD_ID, null);
  },
  getStatus: async ({ commit, dispatch }, data) => {
    const myGen = nextPipelineRecordsFetchGeneration();
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    commit(types.SET_CAMPAIGNS, []);
    commit(types.SET_CURRENT_PIPELINE_BOARD_ID, null);
    commit(types.SET_CURRENT_BOARD_FILTER_PAYLOAD, null);
    try {
      const response = await PipelinesAPI.getstatus(data);
      if (!isLatestPipelineRecordsFetch(myGen)) {
        return response;
      }
      commit(types.SET_CAMPAIGNS, response.data.data);
      hydratePipelineContactLabels(
        { dispatch },
        contactsFromPipelineColumns(response.data.data)
      );
      commit(
        types.SET_CURRENT_PIPELINE_BOARD_ID,
        normalizePipelineBoardId(data?.board_id)
      );
      return response;
    } catch (error) {
      // Ignore error
    } finally {
      if (isLatestPipelineRecordsFetch(myGen)) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
      }
    }
  },
  filterStatus: async ({ commit, dispatch }, data) => {
    const myGen = nextPipelineRecordsFetchGeneration();
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    commit(types.SET_CAMPAIGNS, []);
    commit(types.SET_CURRENT_PIPELINE_BOARD_ID, null);
    try {
      const response = await PipelinesAPI.filterStatus(data);
      if (!isLatestPipelineRecordsFetch(myGen)) {
        return response;
      }
      commit(types.SET_CAMPAIGNS, response.data.data);
      hydratePipelineContactLabels(
        { dispatch },
        contactsFromPipelineColumns(response.data.data)
      );
      commit(
        types.SET_CURRENT_PIPELINE_BOARD_ID,
        normalizePipelineBoardId(data?.board_id)
      );
      commit(types.SET_CURRENT_BOARD_FILTER_PAYLOAD, data.filters ?? null);
      return response;
    } catch (error) {
      console.error("Error filtering statuses:", error);
    } finally {
      if (isLatestPipelineRecordsFetch(myGen)) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
      }
    }
  },
  fetchReminders: async ({ commit }, accountId) => {
    try {
      const response = await axios.get(
        `/api/v1/accounts/${accountId}/get_all_reminders`,
        {
          params: {
            counts_only: true,
          }
        }
      );

      if (response.data) {
        const upcomingCount = response.data.upcoming?.total_count || 0;
        const pastCount = response.data.past?.total_count || 0;

        commit(types.SET_REMINDER_COUNT, upcomingCount);
        commit(types.SET_PAST_REMINDER_COUNT, pastCount);
      }
    } catch (error) {
      commit(types.SET_REMINDER_COUNT, 0);
      commit(types.SET_PAST_REMINDER_COUNT, 0);
      console.error("Error fetching reminders:", error);
    }
  },

  clearContactReminder({ commit }, contactId) {
    commit(types.CLEAR_CONTACT_REMINDER, contactId);
  },

  getStatusFromBoard: async ({ commit },data) => {
    try {
      const response = await PipelinesAPI.getstatus(data);
      console.log("Response",response);
      return response;
    } catch (error) {
      // Ignore error
    }
  },
  createStatus: async ({ commit }, statusObj) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await PipelinesAPI.createstatus(statusObj);
      // console.log("Response",response);
      commit(types.ADD_CAMPAIGN, response.data);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  moveStatus: async ({ commit }, statusObj) => {
    // commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    console.log("My Status Object",statusObj);
    try {
      const response = await PipelinesAPI.movestatus(statusObj);
      // console.log("Response",response);
      // commit(types.ADD_CAMPAIGN, response.data);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  UpdateStatus: async ({ commit }, {id,contact_id}) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      console.log("My Id is", id);
      const response = await PipelinesAPI.updatestatus(id,contact_id);
      // console.log("My Response", response);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  deleteStatus: async ({ commit }, data) => {
    const id=data.status_id;
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      console.log("My Id is", data);
      const response = await PipelinesAPI.deletestatus(data);
      commit(types.DELETE_CAMPAIGN, id);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });

    }
  },
  getState: async ({ commit }) => {
    try {
      const response = await PipelinesAPI.getstate();
      // console.log("Response",response);
      commit(types.SET_STATUS, response.data.states_data);
    } catch (err) {
      console.error("Error while Fetching the data", err);
    }
  },
  getBoards: async ({ commit }) => {
    try {  
      const response = await PipelinesAPI.getboards();
      commit(types.SET_BOARDS, response.data.boards_data);
    } catch (err) {
      console.error("Error while Fetching the data", err);
    }
  },
  getContact: async ({ commit, dispatch }) => {
    try {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingContacts: true });
      const response = await PipelinesAPI.getcontacts();
      // console.log("Response",response);
      commit(types.SET_PIPELINE_CONTACTS, response.data);
      commit(types.SET_PIPELINE_CONTACTS_DATA, response.data.contacts_data);
      hydratePipelineContactLabels(
        { dispatch },
        response.data.contacts_data
      );
    } catch (err) {
      console.error("Error while Fetching the data", err);
    }
    finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingContacts: false });
    }
  },
  updateStatusId: async ({ commit }, { id, updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      console.log("My Id is", id, updateObj);
      const response = await PipelinesAPI.updatestatusid(id, updateObj);
      // console.log("My Response", response);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  DragstatusUpdate: async ({ commit }, { oldindex, index, status_id }) => {
    // commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      console.log("My Id is", index, status_id, oldindex);
      const newIndex = index;
      const stateobj = {
        oldIndex: oldindex,
        newIndex: newIndex
      }
      commit(types.UPDATE_DRAG_STATE, stateobj);
      const response = await PipelinesAPI.dragstatusupdate(index, status_id);
      // console.log("My Response", response);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  FilterContact: async ({ commit, dispatch }, data) => {
    const myGen = nextPipelineRecordsFetchGeneration();
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingContacts: true });
    try {
      console.log("My Data:::::", data);
      const response = await PipelinesAPI.filtercontact(data);
      console.log("Response", response);
      if (!isLatestPipelineRecordsFetch(myGen)) {
        return response;
      }
      commit(types.SET_CAMPAIGNS, response.data.data);
      hydratePipelineContactLabels(
        { dispatch },
        contactsFromPipelineColumns(response.data.data)
      );
      commit(
        types.SET_CURRENT_PIPELINE_BOARD_ID,
        boardIdFromFilterContactData(data)
      );
      return response;
    } catch (err) {
      throw new Error(error);
    } finally {
      if (isLatestPipelineRecordsFetch(myGen)) {
        commit(types.SET_CAMPAIGN_UI_FLAG, {
          isFetchingContacts: false,
          isFetching: false,
        });
      }
    }
  },
  DragcontactUpdate: async ({ commit }, { new_index, contact_id, status_id }) => {
    // commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      console.log("My Id is", new_index, contact_id, status_id);
      const response = await PipelinesAPI.dragcontactupdate(new_index, contact_id, status_id);
      console.log("My Response", response);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  AddState: async ({ commit }, data) => {
    console.log("MY data", data);
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreateState: true });
    try {
      const response = await PipelinesAPI.addstate(data);
      console.log("Response::::", response);
      return response;
    } catch (err) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreateState: false });
    }
  },
  AddBoard: async ({ commit, dispatch }, data) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreateBoard: true });
    try {
      const response = await PipelinesAPI.addboard(data);
      console.log("Response::::", response);
      if (response.status >= 200 && response.status < 300) {
        await dispatch('boards/get', null, { root: true });
      }
      return response;
    } catch (err) {
      throw new Error(err);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreateBoard: false });
    }
  },
  refreshPipelineColumnTotalsFromServer: async (
    { commit, state, rootGetters },
    { statusIds, boardId, administratorAssigneeId }
  ) => {
    const bid = normalizePipelineBoardId(boardId);
    if (!bid || !Array.isArray(statusIds) || statusIds.length === 0) return;

    const activeBoardId = state.currentPipelineBoardId;
    if (activeBoardId != null && Number(bid) !== Number(activeBoardId)) {
      return;
    }

    const ids = [...new Set(statusIds.map(Number).filter(Number.isFinite))];
    const currentUser = rootGetters.getCurrentUser;
    if (!currentUser) return;

    for (const statusId of ids) {
      const data = {
        key: 'status_id',
        value: statusId,
        key6: 'page',
        value6: 1,
        key4: 'board_id',
        value4: bid,
      };
      if (
        currentUser.role === 'administrator' &&
        administratorAssigneeId
      ) {
        data.key1 = 'assignee_id';
        data.value1 = administratorAssigneeId;
      } else if (currentUser.role !== 'administrator') {
        data.key1 = 'assignee_id';
        data.value1 = currentUser.id;
      }
      const payload = { ...data };
      if (state.currentBoardFilterPayload) {
        payload.filters = state.currentBoardFilterPayload;
      }
      try {
        const response = await PipelinesAPI.lazyloading(payload);
        const tc = response?.data?.total_count;
        const sid = normalizeLazyLoadStatusId(data);
        if (sid != null && Number.isFinite(Number(tc))) {
          commit(types.SET_PIPELINE_COLUMN_SERVER_TOTAL, {
            status_id: sid,
            total_count: tc,
          });
        }
      } catch (err) {
        // Non-blocking: keep existing totals; log for ops/debugging.
        console.error(
          '[pipelines] refreshPipelineColumnTotalsFromServer failed',
          { boardId: bid, statusId, error: err }
        );
      }
    }
  },
  Lazyloading: async ({ commit, dispatch, state }, data) => {
    const requestedBoardId = boardIdFromLazyLoadData(data);
    commit(types.SET_CAMPAIGN_UI_FLAG, { islazyloading: true });
    try {
      const payload = { ...data };
      if (state.currentBoardFilterPayload) {
        payload.filters = state.currentBoardFilterPayload;
      }
      const response = await PipelinesAPI.lazyloading(payload);
      const activeBoardId = state.currentPipelineBoardId;
      const rid = requestedBoardId;
      if (rid != null && activeBoardId == null) {
        return response;
      }
      if (rid == null && activeBoardId != null) {
        return response;
      }
      if (
        rid != null &&
        activeBoardId != null &&
        Number(rid) !== Number(activeBoardId)
      ) {
        return response;
      }
      commit(types.SET_PIPELINE_CONTACTS_LAZY_LOADING_DATA, {
        contacts: response.data.contacts_data,
        page: response.data.page,
        total_count: response.data.total_count,
        status_id: normalizeLazyLoadStatusId(data),
      });
      hydratePipelineContactLabels(
        { dispatch },
        response.data.contacts_data
      );
      return response;
    } catch (err) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { islazyloading: false });
    }
  },
  getAssignee: async ({ commit }) => {
    // commit(types.SET_CAMPAIGN_UI_FLAG, { islazyloading: true });
    try {
      const response = await PipelinesAPI.getassignee();
      
      commit(types.SET_AGENTS, response.data.agents);
    
    } catch (err) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { islazyloading: false });
    }
  },
  assignStatus: async ({ commit },data) => {
  
    try {
      const {contactId,statusId,description}=data;
      const response = await PipelinesAPI.assignstatus(contactId,statusId,description);
      return response;
    
    } catch (err) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { islazyloading: false });
    }
  },
  getStatusdata: async ({ commit }, data) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      console.log("My Id is", data);
      const response = await PipelinesAPI.getstatusdata(data);
      // console.log("My Response", response);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  setBoardFilters({ commit }, payload) {
    commit(types.SET_BOARD_FILTERS, payload);
  },
  clearBoardFilters({ commit }, payload) {
    commit(types.CLEAR_BOARD_FILTERS, payload);
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.SET_AGENTS]: MutationHelpers.setAgents,
  [types.SET_STATUS]: MutationHelpers.setStatus,
  [types.SET_BOARDS]: MutationHelpers.setBoards,
  [types.SET_PIPELINE_CONTACTS]: MutationHelpers.setContact,
  [types.SET_PIPELINE_CONTACTS_DATA]: MutationHelpers.setContacttoItems,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
  [types.UPDATE_DRAG_STATE]: MutationHelpers.updatedrag,
  [types.SET_PIPELINE_CONTACTS_LAZY_LOADING_DATA]: MutationHelpers.lazyloadingcontacts,
  [types.SET_PIPELINE_COLUMN_SERVER_TOTAL]: MutationHelpers.setPipelineColumnServerTotal,
  [types.SET_PAST_REMINDER_COUNT]: MutationHelpers.setPastreminder,
  [types.SET_REMINDER_COUNT]: MutationHelpers.setremindercount,
  [types.CLEAR_CONTACT_REMINDER](_state, contactId) {
    if (!contactId || !_state.records || !Array.isArray(_state.records)) return;
    const id = Number(contactId);
    _state.records.forEach(list => {
      if (list.items && Array.isArray(list.items)) {
        const contact = list.items.find(item => Number(item.id) === id);
        if (contact) {
          const attrs = { ...(contact.additional_attributes || {}) };
          delete attrs.calendar_timings;
          Vue.set(contact, 'additional_attributes', attrs);
        }
      }
    });
  },
  [types.SET_BOARD_FILTERS](_state, payload) {
    const hasPayloadShape =
      payload &&
      typeof payload === 'object' &&
      Object.prototype.hasOwnProperty.call(payload, 'filters');

    const filters = hasPayloadShape ? payload.filters : payload;
    const boardId = hasPayloadShape ? payload.boardId : null;

    _state.appliedBoardFilters = Array.isArray(filters) ? filters : [];

    if (boardId) {
      const key = String(boardId);
      _state.appliedBoardFiltersByBoard = {
        ..._state.appliedBoardFiltersByBoard,
        [key]: _state.appliedBoardFilters,
      };
    }
  },
  [types.CLEAR_BOARD_FILTERS](_state, payload) {
    const boardId =
      payload && typeof payload === 'object'
        ? payload.boardId
        : payload;

    _state.appliedBoardFilters = [];
    _state.currentBoardFilterPayload = null;

    if (boardId) {
      const key = String(boardId);
      const nextFiltersByBoard = { ..._state.appliedBoardFiltersByBoard };
      delete nextFiltersByBoard[key];
      _state.appliedBoardFiltersByBoard = nextFiltersByBoard;
    } else {
      _state.appliedBoardFiltersByBoard = {};
    }
  },
  [types.SET_CURRENT_BOARD_FILTER_PAYLOAD](_state, payload) {
    _state.currentBoardFilterPayload = payload;
  },
  [types.SET_CURRENT_PIPELINE_BOARD_ID](_state, boardId) {
    _state.currentPipelineBoardId =
      boardId === undefined || boardId === null || boardId === ''
        ? null
        : Number(boardId);
    if (Number.isNaN(_state.currentPipelineBoardId)) {
      _state.currentPipelineBoardId = null;
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
